'use client'

import { PortableText } from '@portabletext/react'
import { useState } from 'react'

import type { FaqQuestion } from '@/lib/apis/sanity'

type HomeFaqAccordionProps = {
  questions: Array<FaqQuestion & { _id: string }>
  compact?: boolean
}

export function HomeFaqAccordion({
  questions,
  compact = false,
}: HomeFaqAccordionProps) {
  const [openId, setOpenId] = useState<string | null>(questions[0]?._id ?? null)

  return (
    <div className="flex flex-col">
      {questions.map((question) => {
        const isOpen = question._id === openId
        const panelId = `homepage-faq-${question._id}`

        return (
          <div
            key={question._id}
            className="border-b border-[#ECECEF] last:border-b-0"
          >
            <button
              type="button"
              className={`flex w-full items-start justify-between gap-6 text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB] xl:items-center ${
                compact ? 'py-5 xl:py-[22px]' : 'py-[18px] xl:py-6'
              }`}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenId(isOpen ? null : question._id)}
            >
              <span
                className={`min-w-0 flex-1 text-[#0B0B0F] xl:max-w-[720px] ${
                  compact
                    ? 'text-[15px] font-semibold leading-[22px] xl:text-[16px] xl:font-bold'
                    : 'text-[16px] font-bold leading-5 xl:text-[18px] xl:font-semibold xl:leading-[22px]'
                }`}
              >
                {question.question}
              </span>
              <span
                aria-hidden="true"
                className="shrink-0 text-[20px] font-normal leading-6 text-[#2563EB] xl:text-2xl xl:leading-[30px]"
              >
                {isOpen ? '−' : '+'}
              </span>
            </button>
            <div
              id={panelId}
              className={`grid transition-[grid-template-rows,opacity] duration-700 ease-[cubic-bezier(0.22,0.55,0.28,1)] ${
                isOpen
                  ? 'grid-rows-[1fr] opacity-100'
                  : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="pb-[18px] text-[14px] leading-[21px] text-[#82828B] xl:max-w-[720px] xl:pb-6 xl:text-[15px] xl:leading-6 [&_a]:text-[#2563EB] [&_a]:underline [&_p+p]:mt-3">
                  <PortableText value={question.answer} />
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
